import { Linkedin, Twitter, Facebook, Link, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface SocialShareProps {
  title: string;
  url?: string;
  variant?: 'default' | 'light';
  className?: string;
}

export const SocialShare = ({ title, url, variant = 'default', className = '' }: SocialShareProps) => {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  
  const shareLinks = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=Check out this article: ${encodedUrl}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const openShare = (platform: keyof typeof shareLinks) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
  };

  const buttonClass = variant === 'light' 
    ? 'bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/20' 
    : '';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className={`text-sm ${variant === 'light' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
        Share:
      </span>
      <Button 
        variant="outline" 
        size="icon" 
        className={`h-8 w-8 ${buttonClass}`}
        onClick={() => openShare('linkedin')}
        title="Share on LinkedIn"
      >
        <Linkedin className="h-4 w-4" />
      </Button>
      <Button 
        variant="outline" 
        size="icon" 
        className={`h-8 w-8 ${buttonClass}`}
        onClick={() => openShare('twitter')}
        title="Share on X (Twitter)"
      >
        <Twitter className="h-4 w-4" />
      </Button>
      <Button 
        variant="outline" 
        size="icon" 
        className={`h-8 w-8 ${buttonClass}`}
        onClick={() => openShare('facebook')}
        title="Share on Facebook"
      >
        <Facebook className="h-4 w-4" />
      </Button>
      <Button 
        variant="outline" 
        size="icon" 
        className={`h-8 w-8 ${buttonClass}`}
        onClick={() => openShare('email')}
        title="Share via Email"
      >
        <Mail className="h-4 w-4" />
      </Button>
      <Button 
        variant="outline" 
        size="icon" 
        className={`h-8 w-8 ${buttonClass}`}
        onClick={copyToClipboard}
        title="Copy link"
      >
        <Link className="h-4 w-4" />
      </Button>
    </div>
  );
};
